import classNames from 'classnames';
import { useSelect } from 'downshift';
import React, { FC, memo } from 'react';
import getI18N from '../../lib/i18n';
import styles from './DropdownSelect.module.scss';

export interface DropdownOption {
    text: string;
    value: string;
}

export interface DropdownSelectProps {
    options: DropdownOption[];
    onChange?: (value: string) => void;
    active?: string;
    label?: string | null;
    emptyError?: string | null;
    text?: string;
    className?: string;
    width?: 'auto';
    dropdownType?: 'spread';
}

const i18n = getI18N();

const DropDownSelect: FC<DropdownSelectProps> = ({
    options,
    active: defaultActive = options[0]?.value ?? '',
    onChange: defaultOnChange,
    label,
    emptyError = '',
    text = '---',
    width,
    dropdownType,
}): JSX.Element => {
    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        setHighlightedIndex,
    } = useSelect<DropdownOption>({
        items: options,
        itemToString: (item: DropdownOption | null) => item?.text ?? '',
        onSelectedItemChange: (data) => {
            const index = options.findIndex((item) => item.value === defaultActive);
            setHighlightedIndex(index);
            if (defaultOnChange) defaultOnChange(data.selectedItem?.value ?? '');
        },
        initialSelectedItem: options.find((item) => item.value === defaultActive),
        initialHighlightedIndex: options.findIndex((item) => item.value === defaultActive),
    });
    return (
        <div
            className={classNames(styles['dropdown-container'], width && styles[`width-${width}`])}
        >
            {label && (
                <span className={styles.label} {...getLabelProps}>
                    {label}
                </span>
            )}
            <div className={styles['button-group']}>
                <button
                    type="button"
                    {...getToggleButtonProps()}
                    data-testid="dropdown-select"
                    className={styles['dropdown-button']}
                    aria-label="toggle dropdown"
                >
                    {options.length ? i18n(text) ?? '---' : '---'}
                </button>
                <span className={styles['dropdown-icon']} />
            </div>
            {!options.length && emptyError && <span className={styles.error}>{emptyError}</span>}
            <ul
                className={classNames(
                    styles.dropdown,
                    {
                        [styles.visible]: isOpen && options.length > 0,
                    },
                    dropdownType && styles[dropdownType]
                )}
                data-testid="items-ul"
                {...getMenuProps()}
            >
                {isOpen &&
                    options.map((item, index) => (
                        <li
                            className={classNames(
                                styles.item,
                                {
                                    [styles.highlight]: highlightedIndex === index,
                                    [styles.selected]: item.value === selectedItem?.value,
                                },
                                dropdownType === 'spread' && styles.narrow
                            )}
                            key={item.value}
                            {...getItemProps({
                                item,
                                index,
                            })}
                        >
                            {i18n(item.text)}
                        </li>
                    ))}
            </ul>
        </div>
    );
};

export default memo(DropDownSelect);
